import { useState, type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Alert } from '@interstate/components/Alert';
import { Action } from '@interstate/components/Action';
import { Box } from '@interstate/components/Box';
import { Typography } from '@interstate/components/Typography';

import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import { actionCreators } from '@makemydeal/dr-offer-redux';

export type CviPricingUpdate = {
    cost?: number;
    invoiceAmount?: number;
};

export type CviCostMismatchAlertProps = {
    onUpdate?: (update: CviPricingUpdate) => void;
};

type CviPricingChange = {
    current: number;
    cvi: number;
};

const toPricingChange = (hasMismatch: boolean, current?: number, cvi?: number): CviPricingChange | undefined =>
    hasMismatch && current != null && cvi != null ? { current, cvi } : undefined;

const describeChange = ({ current, cvi }: CviPricingChange) =>
    `from ${formatDollarsAndCents(current)} to ${formatDollarsAndCents(cvi)}`;

type CviAlertContent = {
    message: string;
    changes?: string[];
    linkText: string;
};

const getAlertContent = (costChange?: CviPricingChange, invoiceChange?: CviPricingChange): CviAlertContent | undefined => {
    if (costChange && invoiceChange) {
        return {
            message: 'The Cost and Invoice for this vehicle have changed since this deal was last saved:',
            changes: [`Cost: ${describeChange(costChange)}`, `Invoice: ${describeChange(invoiceChange)}`],
            linkText: 'Update deal cost and invoice'
        };
    }
    if (costChange) {
        return {
            message: `The Cost for this vehicle has changed ${describeChange(costChange)} since this deal was last saved.`,
            linkText: 'Update deal cost'
        };
    }
    if (invoiceChange) {
        return {
            message: `The Invoice for this vehicle has changed ${describeChange(invoiceChange)} since this deal was last saved.`,
            linkText: 'Update deal invoice'
        };
    }
    return undefined;
};

export const CviCostMismatchAlert: FC<CviCostMismatchAlertProps> = ({ onUpdate }) => {
    const dispatch = useDispatch();
    const hasCostMismatch = useSelector(vehicleSelectors.hasCviCostMismatch);
    const hasInvoiceMismatch = useSelector(vehicleSelectors.hasCviInvoiceMismatch);
    const currentCost = useSelector(vehicleSelectors.getVehicleCost);
    const cviCost = useSelector(vehicleSelectors.getCviCost);
    const currentInvoice = useSelector(vehicleSelectors.getInvoiceAmount);
    const cviInvoice = useSelector(vehicleSelectors.getCviInvoiceAmount);
    const [hidden, setHidden] = useState(false);

    const costChange = toPricingChange(hasCostMismatch, currentCost, cviCost);
    const invoiceChange = toPricingChange(hasInvoiceMismatch, currentInvoice, cviInvoice);
    const content = getAlertContent(costChange, invoiceChange);

    if (hidden || !content) {
        return null;
    }

    const update: CviPricingUpdate = {
        ...(costChange && { cost: costChange.cvi }),
        ...(invoiceChange && { invoiceAmount: invoiceChange.cvi })
    };

    const handleUpdate = () => {
        if (onUpdate) {
            onUpdate(update);
            setHidden(true);
        } else {
            dispatch(actionCreators.updateVehicleInfoDetails({ vehicle: update }, true));
        }
    };

    const updateLink = (
        <Action data-testid="update-deal-cost-link" onClick={handleUpdate}>
            <Typography variant="body-sm" sx={{ color: '#005ba8', textDecoration: 'underline' }}>
                {content.linkText}
            </Typography>
        </Action>
    );

    return (
        <Alert type="info" data-testid="cvi-cost-mismatch-alert" sx={{ margin: '0.5rem 0' }}>
            {content.message}
            {content.changes ? (
                <>
                    <Box component="ul" data-testid="cvi-cost-mismatch-changes" sx={{ margin: '0.25rem 0', paddingLeft: '1.5rem' }}>
                        {content.changes.map((change) => (
                            <li key={change}>{change}</li>
                        ))}
                    </Box>
                    {updateLink}
                </>
            ) : (
                <> {updateLink}</>
            )}
        </Alert>
    );
};
